import { useEffect, useRef, useState } from 'react';
import { Check, Cloud, CloudOff, Copy, Eye, EyeOff, LogIn, Plus, RefreshCw, Trash2, Undo2 } from 'lucide-react';
import type { Action } from '@airmail/engine';
import { useSearchParams } from 'react-router';
import { useSync, useTurnier } from '@/store/TournamentProvider';
import { useAuth } from '@/store/AuthProvider';
import { addHelper, DEFAULT_API, ensureViewKey, helperLink, joinLink, listHelpers, removeHelper, rotateKey, viewLink, type HelperKey, type SyncConfig } from '@/store/sync';
import { qrSvg } from '@/lib/qr';
import { currentStateOf, describeAction, subjectOf } from '@/lib/actionText';
import { Button, Card, Field, Input, Select } from '@/ui';
import { alertDialog, confirmDialog, copyDialog } from '@/ui/dialogs';
import { fmtTime } from '@/lib/format';
import { tm } from '@/i18n/engine';
import { useT } from '@/i18n';
import { fehlerText, stufenHinweis, usePlan } from '@/store/plan';
import { Gate, useFreigabe } from '@/components/ProMarke';

/** Cloud-Sync-Abschnitt in den Einstellungen (docs/14 §4) — braucht ein Organisatoren-Konto (docs/16) */
export function SyncPanel() {
  const t = useT();
  const { tournament, patchSyncCfg } = useTurnier();
  const sync = useSync();
  const auth = useAuth();
  const [api, setApi] = useState(DEFAULT_API);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<'join' | 'view' | null>(null);
  const [qr, setQr] = useState('');
  /*
   * Der Helfer-Link ist ein Schlüssel: Wer ihn abfotografiert, trägt Ergebnisse
   * ein. Harald: „alle Vorgänge auf dem Laptop sind auf einem Bildschirm für
   * alle sichtbar." Deshalb liegen Link und QR-Code zu, bis jemand sie
   * ausdrücklich aufdeckt — der Zuschauer-Link bleibt offen, der darf gesehen
   * werden.
   */
  const [helferSichtbar, setHelferSichtbar] = useState<string | null>(null);
  /*
   * Helfer-Links (docs/notes/helfer.md). Jeder Helfer bekommt einen **eigenen**
   * Schlüssel vom Server; Umfang und Ort hängen am Schlüssel, nicht an der
   * Adresse. Zurückziehen trifft nur diesen einen — die Leitung muss nichts
   * neu verteilen.
   */
  const [helpers, setHelpers] = useState<HelperKey[] | null>(null);
  // Limit der Stufe (docs/21): Standard 3 Helfer je Turnier, Pro ohne Limit — der Server prüft es ebenso (402)
  const { limits } = usePlan();
  const helferErlaubt = useFreigabe('helfer', helpers?.length ?? 0);
  const [neuName, setNeuName] = useState('');
  const [neuOrt, setNeuOrt] = useState('');
  const [joinQr, setJoinQr] = useState('');
  const [leitungSichtbar, setLeitungSichtbar] = useState(false);
  // Plätze des Turniers — ein Helfer wird meist auf genau einen festgelegt
  const courtGroups = [...new Set(tournament.courts.filter((c) => c.active && c.group).map((c) => c.group as string))].sort((a, b) => a.localeCompare(b));

  // Das Sync-Profil ändert sich bei jedem Abgleich — die Effekte unten sollen
  // nur bei Turnier oder Schlüssel neu laufen, lesen den Rest aber aktuell
  const cfgRef = useRef(sync.cfg);
  cfgRef.current = sync.cfg;

  // Zuschauer-Link (docs/17 §9): einmal vom Server holen, dann im Sync-Profil merken
  const viewKey = sync.cfg?.viewKey;
  const cfgId = sync.cfg?.tournamentId;
  const [viewKeyFehler, setViewKeyFehler] = useState<string | null>(null);
  useEffect(() => {
    const cfg = cfgRef.current;
    if (!cfg || viewKey || !navigator.onLine) return;
    let alive = true;
    ensureViewKey(cfg)
      .then((k) => {
        if (!alive) return;
        setViewKeyFehler(null);
        patchSyncCfg({ viewKey: k });
      })
      // Vorher verschluckt: Dann stand für immer „Wird angelegt, sobald Internet da ist …" — auch mit Netz
      .catch((e: Error) => alive && setViewKeyFehler(e.message));
    return () => {
      alive = false;
    };
  }, [cfgId, viewKey, patchSyncCfg]);
  const vLink = sync.cfg && viewKey ? viewLink(sync.cfg, viewKey) : '';

  // Liste der ausgegebenen Helfer-Links vom Server. Ein Netzfehler ist **nicht**
  // „keine Helfer" — sonst legt die Leitung neue an, obwohl welche existieren.
  const cfgKey = sync.cfg?.key;
  const [helferFehler, setHelferFehler] = useState<string | null>(null);
  const [holenNr, setHolenNr] = useState(0);
  useEffect(() => {
    const cfg = cfgRef.current;
    if (!cfg || !navigator.onLine) return;
    let alive = true;
    listHelpers(cfg)
      .then((r) => {
        if (!alive) return;
        setHelpers(r.helpers);
        setHelferFehler(null);
      })
      .catch((e: Error) => alive && setHelferFehler(e.message));
    return () => {
      alive = false;
    };
  }, [cfgId, cfgKey, holenNr]);

  const sichtbarerLink = sync.cfg && helferSichtbar ? helperLink(sync.cfg, helferSichtbar) : '';
  useEffect(() => {
    if (!sichtbarerLink) return setJoinQr('');
    let alive = true;
    qrSvg(sichtbarerLink, { size: 160 })
      .then((svg) => alive && setJoinQr(svg))
      .catch(() => alive && setJoinQr(''));
    return () => {
      alive = false;
    };
  }, [sichtbarerLink]);

  useEffect(() => {
    if (!vLink) return setQr('');
    let alive = true;
    qrSvg(vLink, { size: 160 })
      .then((svg) => alive && setQr(svg))
      .catch(() => alive && setQr(''));
    return () => {
      alive = false;
    };
  }, [vLink]);

  const enable = async () => {
    setBusy(true);
    try {
      await sync.enable(api.trim());
    } catch (e) {
      // 402 bei Stufe `frei`: die Erklärung mit Preis statt „Nur in Standard"
      await alertDialog(t('Aktivieren fehlgeschlagen: {e}', { e: t(fehlerText(e)) }));
    } finally {
      setBusy(false);
    }
  };

  if (!sync.cfg) {
    return (
      <Card title={t('Cloud-Sync')}>
        <div className="p-4 grid gap-3 text-sm">
          <p className="m-0 text-muted">
            {t(
              'Aus: Das Turnier liegt nur auf diesem Gerät. Mit Cloud-Sync wird jede Aktion bei Internet an den Server gespiegelt — als Sicherung und damit Helfer per Handy Ergebnisse eintragen können. Ohne Netz läuft alles weiter und wird nachgereicht.',
            )}
          </p>
          {!DEFAULT_API && (
            <Field label={t('Sync-Adresse (API-URL)')} hint={t('Steht nach dem Deploy in der Ausgabe von scripts/deploy.sh — oder ist im Build fest eingetragen.')}>
              <Input value={api} onChange={(e) => setApi(e.target.value)} placeholder="https://xxxx.execute-api.eu-central-1.amazonaws.com" />
            </Field>
          )}
          {auth.available && !auth.user ? (
            <div className="grid gap-2">
              <p className="m-0 text-muted">{t('Das Turnier wird deinem Organisatoren-Konto zugeordnet — nur du und deine Helfer mit Link kommen an die Daten.')}</p>
              <div>
                <Button variant="primary" onClick={() => void auth.login(`/t/${tournament.id}/einstellungen?tab=cloud`)}>
                  <LogIn size={14} /> {t('Als Organisator anmelden')}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button variant="primary" onClick={enable} disabled={busy || !api.trim() || auth.loading}>
                <Cloud size={14} /> {t('Cloud-Sync aktivieren')}
              </Button>
              {auth.user && <span className="text-xs text-dim ml-3">{t('angemeldet als {who}', { who: auth.user.email ?? auth.user.sub })}</span>}
            </div>
          )}
        </div>
      </Card>
    );
  }

  const copy = async (which: 'join' | 'view', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      await copyDialog(text);
    }
  };

  /*
   * Vier Kästen statt einem (Daniel, 09/2026: „das Helfer-Link-Erstellen geht
   * unter, Leitungs-Link vielleicht als eigener Kasten?"): Helfer — Spieler &
   * Zuschauer — Leitung auf weiteren Geräten — Verbindung. Jeder Kasten sagt
   * in einem Satz, für wen er ist; Anlegen steht oben, die Liste darunter.
   */
  return (
    <div className="grid gap-4">
      <Card title={t('Helfer-Links')} aside={helpers && helpers.length > 0 ? <span className="label">{helpers.length === 1 ? t('1 Link') : t('{n} Links', { n: helpers.length })}</span> : undefined}>
        <div className="p-4 grid gap-3 text-sm">
          <p className="m-0 text-xs text-muted">
            {t(
              'Je Helfer ein eigener Link. Er sieht nur Spiele und Tabelle und darf nur Ergebnisse eintragen und Spiele starten — das prüft der Server, nicht die App: An der Adresse zu drehen bringt nichts. Einen Link zurückzuziehen trifft nur diesen einen Helfer.',
            )}
          </p>
          <div className="border border-blue/60 bg-blue/5 px-3 py-3 grid gap-2">
            <span className="label">{t('Neuen Helfer-Link anlegen')}</span>
            {!helferErlaubt && (
              <span className="text-xs text-warn" data-helfer-limit={limits.helfer ?? ''}>
                {t(stufenHinweis('helfer'))}
              </span>
            )}
            <Gate feature="helfer" anzahl={helpers?.length ?? 0} block>
              <form
                className="flex gap-2 flex-wrap items-center"
                onSubmit={async (e) => {
                  e.preventDefault();
                  // Enter im Feld darf das Limit nicht umgehen — das Gate sperrt nur Klick und Fokus
                  if (!sync.cfg || !helferErlaubt) return;
                  try {
                    const h = await addHelper(sync.cfg, { label: neuName.trim(), ...(neuOrt ? { courtGroup: neuOrt } : {}) });
                    setHelpers([...(helpers ?? []), h]);
                    setHelferSichtbar(h.key);
                    setNeuName('');
                  } catch (err) {
                    await alertDialog(t(fehlerText(err)));
                  }
                }}
              >
                <Input className="w-44 flex-1 min-w-[10rem]" placeholder={t('Name, z. B. Marco')} value={neuName} onChange={(e) => setNeuName(e.target.value)} aria-label={t('Name des Helfers')} />
                {courtGroups.length > 0 && (
                  <Select className="w-auto" value={neuOrt} onChange={(e) => setNeuOrt(e.target.value)} aria-label={t('Platz des Helfers')}>
                    <option value="">{t('alle Plätze')}</option>
                    {courtGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </Select>
                )}
                <Button type="submit" variant="primary">
                  <Plus size={14} /> {t('Erstellen')}
                </Button>
              </form>
            </Gate>
          </div>
          {/* Ausgegebene Links: ein Kasten mit Zeilen — nicht je Helfer ein eigener Kasten */}
          <div className="grid gap-1.5">
            <span className="label">{t('Ausgegebene Helfer-Links')}</span>
            <div className="border border-line bg-panel">
              {helferFehler ? (
                <div className="px-3 py-3 text-xs text-warn flex items-center gap-2 flex-wrap">
                  <span>{t('Liste konnte nicht geladen werden: {e}', { e: helferFehler })}</span>
                  <Button size="sm" variant="ghost" onClick={() => setHolenNr((n) => n + 1)}>
                    {t('Erneut versuchen')}
                  </Button>
                </div>
              ) : (
                (helpers ?? []).length === 0 && <div className="px-3 py-3 text-xs text-dim">{t('Noch kein Helfer-Link ausgegeben.')}</div>
              )}
              {(helpers ?? []).map((h) => (
                <div key={h.key} className="px-3 py-2 grid gap-2 border-b border-line2 last:border-b-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <b className="tracking-tight">{h.label}</b>
                    {h.courtGroup && <span className="label">{h.courtGroup}</span>}
                    <div className="flex-1" />
                    <Button size="sm" variant="ghost" onClick={() => setHelferSichtbar(helferSichtbar === h.key ? null : h.key)}>
                      {helferSichtbar === h.key ? <EyeOff size={13} /> : <Eye size={13} />} {helferSichtbar === h.key ? t('Verbergen') : t('Link zeigen')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('Link von {name} zurückziehen', { name: h.label })}
                      title={t('Diesen Helfer aussperren — die anderen Links bleiben gültig')}
                      onClick={async () => {
                        if (
                          !sync.cfg ||
                          !(await confirmDialog(t('Link von „{name}“ zurückziehen? Sein Gerät kommt danach nicht mehr hinein.', { name: h.label }), { ok: t('Zurückziehen'), danger: true }))
                        )
                          return;
                        try {
                          setHelpers((await removeHelper(sync.cfg, h.key)).helpers);
                          if (helferSichtbar === h.key) setHelferSichtbar(null);
                        } catch (e) {
                          await alertDialog((e as Error).message);
                        }
                      }}
                    >
                      <Trash2 size={13} /> {t('Zurückziehen')}
                    </Button>
                  </div>
                  {helferSichtbar === h.key && (
                    <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
                      <div
                        data-qr
                        className="bg-white p-1.5 border border-line"
                        style={{ width: 132, height: 132 }}
                        dangerouslySetInnerHTML={{ __html: joinQr }}
                        title={t('QR-Code zum Helfer-Link')}
                      />
                      <div className="grid gap-2">
                        <div className="flex gap-2">
                          <Input readOnly value={sichtbarerLink} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
                          <Button onClick={() => copy('join', sichtbarerLink)}>
                            <Copy size={14} /> {copied === 'join' ? t('Kopiert') : t('Kopieren')}
                          </Button>
                        </div>
                        <div className="text-xs text-dim">{t('Hinhalten und abscannen — oder den Link schicken.')}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card title={t('Spieler & Zuschauer')}>
        <div className="p-4 grid gap-3 text-sm">
          <p className="m-0 text-xs text-muted">
            {t(
              'Ein Link für alle, ohne Konto und nur zum Mitlesen: Spieler wählen ihr Team und sehen, wann und wo sie dran sind — Zuschauer sehen alle Spiele, Tabellen und Ergebnisse. Aushängen oder in die Gruppe schicken.',
            )}
          </p>
          {vLink ? (
            <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
              <div data-qr className="bg-white p-1.5 border border-line" style={{ width: 132, height: 132 }} dangerouslySetInnerHTML={{ __html: qr }} title={t('QR-Code zum Zuschauer-Link')} />
              <div className="grid gap-2">
                <div className="flex gap-2">
                  <Input readOnly value={vLink} onFocus={(e) => e.target.select()} className="font-mono text-xs" aria-label={t('Link für Spieler und Zuschauer')} />
                  <Button onClick={() => copy('view', vLink)}>
                    <Copy size={14} /> {copied === 'view' ? t('Kopiert') : t('Kopieren')}
                  </Button>
                </div>
                <div>
                  <a href={vLink} target="_blank" rel="noreferrer" className="no-underline">
                    <Button variant="ghost" size="sm">
                      <Eye size={13} /> {t('Ansicht öffnen')}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          ) : viewKeyFehler ? (
            <div className="text-warn text-xs">{t('Zuschauer-Link konnte nicht angelegt werden: {e}', { e: viewKeyFehler })}</div>
          ) : (
            <div className="text-dim text-xs">{t('Wird angelegt, sobald Internet da ist …')}</div>
          )}
        </div>
      </Card>

      <Card title={t('Leitung auf weiteren Geräten')}>
        <div className="p-4 grid gap-3 text-sm">
          <p className="m-0 text-xs text-muted">{t('Voller Zugriff wie auf diesem Gerät — nur für die Turnierleitung selbst. Nicht an Helfer geben; dafür sind die Helfer-Links da.')}</p>
          {leitungSichtbar && sync.cfg ? (
            <div className="flex gap-2">
              <Input readOnly value={joinLink(sync.cfg)} onFocus={(e) => e.target.select()} className="font-mono text-xs" aria-label={t('Leitungs-Link')} />
              <Button onClick={() => copy('join', joinLink(sync.cfg as SyncConfig))}>
                <Copy size={14} /> {copied === 'join' ? t('Kopiert') : t('Kopieren')}
              </Button>
              <Button variant="ghost" onClick={() => setLeitungSichtbar(false)} aria-label={t('Link verbergen')}>
                <EyeOff size={13} />
              </Button>
            </div>
          ) : (
            <div>
              <Button onClick={() => setLeitungSichtbar(true)}>
                <Eye size={14} /> {t('Leitungs-Link anzeigen')}
              </Button>
            </div>
          )}
          {/* Nur mit Konto: Der Server wechselt den Schlüssel nur für Eigentümer und Mitorganisatoren */}
          {auth.user && (
            <div>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                title={t('Wer den alten Link hat, kommt danach nicht mehr hinein — Helfer-Links und Zuschauer-Link bleiben')}
                onClick={async () => {
                  if (
                    !sync.cfg ||
                    !(await confirmDialog(
                      t('Schlüssel wechseln? Ein zweites Leitungsgerät und alte Leitungs-Links verlieren den Zugriff und müssen neu beitreten. Helfer-Links und Zuschauer-Link bleiben gültig.'),
                      { ok: t('Schlüssel wechseln'), danger: true },
                    ))
                  )
                    return;
                  setBusy(true);
                  try {
                    const key = await rotateKey(sync.cfg);
                    patchSyncCfg({ key });
                    setLeitungSichtbar(true);
                  } catch (e) {
                    await alertDialog((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <RefreshCw size={13} /> {t('Schlüssel wechseln')}
              </Button>
            </div>
          )}
          <p className="m-0 text-xs text-dim">{t('Mit Konto geht es auch ohne Link: auf dem anderen Gerät anmelden, das Turnier steht dann unter „In der Cloud“.')}</p>
        </div>
      </Card>

      <Card title={t('Verbindung')} aside={<StatusText />}>
        <div className="p-4 grid gap-3 text-sm">
          {sync.cfg.rejected.length > 0 && <RejectedList />}
          {sync.lastError && <div className="text-err text-xs">{t('Letzter Fehler: {e}', { e: tm(sync.lastError) })}</div>}
          {/* Technik: braucht man nur, wenn etwas klemmt — darum zugeklappt */}
          <details className="border border-line2">
            <summary className="label cursor-pointer px-3 py-2 select-none">{t('Technik & Verbindung')}</summary>
            <div className="px-3 pb-3 grid gap-3">
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-[12px]">
                <span className="text-dim">{t('Server')}</span>
                <span className="truncate">{sync.cfg.apiUrl}</span>
                <span className="text-dim">{t('Turnier-ID')}</span>
                <span>{sync.cfg.tournamentId}</span>
                <span className="text-dim">{t('Schlüssel')}</span>
                <span>{sync.cfg.key}</span>
                <span className="text-dim">{t('Stand')}</span>
                <span>
                  {sync.lastSyncAt
                    ? t('{n} Aktionen auf dem Server · {p} ausstehend · zuletzt {time}', { n: sync.cfg.seq, p: sync.pending, time: fmtTime(sync.lastSyncAt) })
                    : t('{n} Aktionen auf dem Server · {p} ausstehend', { n: sync.cfg.seq, p: sync.pending })}
                </span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => void sync.syncNow()} disabled={sync.status === 'syncing'}>
                  <RefreshCw size={14} /> {t('Jetzt synchronisieren')}
                </Button>
                <Button
                  variant="danger"
                  onClick={async () =>
                    (await confirmDialog(t('Sync auf diesem Gerät beenden? Das Turnier bleibt lokal und auf dem Server erhalten.'), { ok: t('Sync beenden'), danger: true })) && void sync.disable()
                  }
                >
                  <CloudOff size={14} /> {t('Sync beenden')}
                </Button>
              </div>
            </div>
          </details>
        </div>
      </Card>
    </div>
  );
}

/**
 * Verworfene eigene Aktionen (docs/notes/konflikte.md).
 *
 * Zwei Geräte haben gleichzeitig etwas getan, das sich widerspricht — etwa
 * dasselbe Spiel gewertet. Der Server bestimmt die Reihenfolge; was danach
 * nicht mehr passt, fällt heraus. Bisher stand hier nur eine Zeile Technik.
 * Jetzt steht da, was es war, warum es nicht ging, und der Knopf, es doch noch
 * anzuwenden — das Gegenstück zum „Merge", nur von Hand entschieden: Die
 * Leitung sieht beide Stände und sagt, welcher gilt.
 */
function RejectedList() {
  const t = useT();
  const { tournament, dispatch, patchSyncCfg } = useTurnier();
  const sync = useSync();
  const list = sync.cfg?.rejected ?? [];
  // Vom Hinweis aus („…&konflikt=1") gleich hierher rollen — sonst steht der
  // Kasten unter Server, Links und QR-Code und man sucht ihn.
  const box = useRef<HTMLDivElement>(null);
  const [params] = useSearchParams();
  const jump = params.get('konflikt') === '1';
  useEffect(() => {
    if (jump) box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [jump]);

  // Nach Position, nicht nach Zeitstempel: Alle Einträge eines Abgleichs tragen
  // dieselbe Zeit — sonst verschwänden mit einem Klick gleich mehrere.
  const drop = (i: number) => patchSyncCfg({ rejected: list.filter((_, k) => k !== i) });
  const retry = (i: number) => {
    const entry = list[i];
    if (!entry?.action) return;
    // Ohne die alte Kennung: Das ist ein neuer Versuch auf dem jetzigen Stand,
    // keine Wiederholung der alten Aktion.
    const { id: _id, at: _at, actor: _actor, ...body } = entry.action as Action & { actor?: string };
    if (dispatch(body).ok) drop(i);
  };

  return (
    <div ref={box} className="border border-warn">
      <div className="px-3 py-2 border-b border-warn/50 flex items-center gap-2 flex-wrap">
        <b className="text-warn text-sm">{t('Von einem anderen Gerät überholt')}</b>
        <span className="text-xs text-muted">{t('{n} eigene Eingaben passten nicht mehr', { n: list.length })}</span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => patchSyncCfg({ rejected: [] })}>
          {t('Liste leeren')}
        </Button>
      </div>
      <p className="m-0 px-3 py-2 text-xs text-muted leading-relaxed border-b border-warn/30">
        {t(
          'Zwei Geräte haben gleichzeitig etwas Widersprüchliches eingetragen — meist dasselbe Spiel. Es gilt, was zuerst beim Server war. Links steht, was jetzt gilt, rechts deine zurückgenommene Eingabe: „So lassen" behält den jetzigen Stand, „Meine Eingabe übernehmen" trägt deinen jetzt ein und schickt ihn an alle Geräte.',
        )}
      </p>
      <ul className="m-0 p-0 list-none">
        {list.map((r, i) => (
          <li key={r.action?.id ?? `${r.at}-${i}`} className="px-3 py-3 border-b border-line2 last:border-b-0 grid gap-2 text-sm">
            <div className="flex items-baseline gap-2 flex-wrap">
              <b className="tracking-tight">{r.action ? subjectOf(r.action, tournament) : r.type}</b>
              <span className="font-mono text-[11px] text-dim">{fmtTime(r.at)}</span>
            </div>
            {/* Beide Stände nebeneinander — links, was gilt, rechts, was du wolltest */}
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="border border-live/60 bg-panel2 px-3 py-2 flex flex-col">
                <div className="label mb-1 text-live">{t('Jetzt gültig')}</div>
                <div className="font-bold tracking-tight">{r.action ? currentStateOf(r.action, tournament) : tm(r.error)}</div>
                <div className="text-xs text-muted mt-0.5 flex-1">{t('So steht es auf allen Geräten.')}</div>
                <Button size="sm" className="mt-2 w-full" onClick={() => drop(i)}>
                  <Check size={12} /> {t('So lassen')}
                </Button>
              </div>
              <div className="border border-warn/60 bg-panel2 px-3 py-2 flex flex-col">
                <div className="label mb-1 text-warn">{t('Deine Eingabe')}</div>
                <div className="font-bold tracking-tight">{r.action ? describeAction(r.action, tournament) : r.type}</div>
                <div className="text-xs text-muted mt-0.5 flex-1">{t('Zurückgenommen: {grund}', { grund: tm(r.error) })}</div>
                <Button size="sm" variant="primary" className="mt-2 w-full" disabled={!r.action} onClick={() => retry(i)}>
                  <Undo2 size={12} /> {t('Meine Eingabe übernehmen')}
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StatusText() {
  const t = useT();
  const sync = useSync();
  const map: Record<typeof sync.status, [string, string]> = {
    off: [t('aus'), 'text-dim'],
    idle: [t('synchron'), 'text-live'],
    syncing: [t('synchronisiere …'), 'text-blue'],
    pending: [t('{n} ausstehend', { n: sync.pending }), 'text-warn'],
    offline: [t('offline — wird nachgereicht'), 'text-warn'],
    error: [t('Fehler'), 'text-err'],
  };
  const [label, cls] = map[sync.status];
  return <span className={`label ${cls}`}>{label}</span>;
}
